<template>
  <section class="car-detail-section" id="car-detail">
    <div class="container" v-if="loading">
      <div class="loading-skeleton">
        <div class="skeleton-header">
          <div class="skeleton skeleton-breadcrumb"></div>
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-underline"></div>
          <div class="skeleton skeleton-price"></div>
        </div>
        <div class="skeleton-gallery">
          <div class="skeleton skeleton-main-image"></div>
          <div class="skeleton-thumbnails">
            <div
              class="skeleton skeleton-thumbnail"
              v-for="n in 4"
              :key="n"
            ></div>
          </div>
        </div>
        <div class="skeleton-specs">
          <div class="skeleton skeleton-specs-title"></div>
          <div class="skeleton-specs-grid">
            <div
              class="skeleton skeleton-spec-item"
              v-for="n in 4"
              :key="n"
            ></div>
          </div>
        </div>
        <div class="skeleton-form">
          <div class="skeleton skeleton-form-title"></div>
          <div class="skeleton-form-grid">
            <div class="skeleton skeleton-input"></div>
            <div class="skeleton skeleton-input"></div>
            <div class="skeleton skeleton-input"></div>
          </div>
          <div class="skeleton skeleton-button"></div>
        </div>
      </div>
    </div>

    <div class="container" v-else-if="!car && !loading && !error">
      <div class="error-message">
        No car data available. Please try again later.
      </div>
    </div>

    <div class="container" v-else-if="car">
      <div class="section-header" data-aos="fade-up" data-aos-duration="800">
        <div class="breadcrumb">
          <a href="/">Home</a> /
          <span>{{ car.manufacturer }} {{ car.model }}</span>
        </div>
        <h1 class="section-title">
          {{ car.year }} {{ car.manufacturer }} {{ car.model }}
        </h1>
        <div class="title-underline"></div>

        <!-- Pricing for Lease Cars -->
        <div v-if="car.is_lease" class="car-lease-pricing">
          <div class="lease-badge">Available for Lease</div>
          <div class="lease-prices">
            <div
              v-if="car.lease_info.lease_price_per_month"
              class="lease-price-option"
            >
              <span class="price-label">Monthly payment:</span>
              <span class="price-value"
                >${{ car.lease_info.lease_price_per_month }}/month</span
              >
            </div>
            <div
              v-if="car.lease_info.lease_price_per_day"
              class="lease-price-option"
            >
              <span class="price-label">Downpayment:</span>
              <span class="price-value"
                >${{ car.lease_info.lease_price_per_day }}</span
              >
            </div>
          </div>
        </div>

        <!-- Pricing for Purchase Cars -->
        <div v-else class="car-price">
          <span class="price-tag"
            >${{
              (car.has_sale ? car.sale_price : car.price).toLocaleString()
            }}</span
          >
          <span v-if="car.has_sale" class="original-price"
            >${{ car.price.toLocaleString() }}</span
          >
        </div>
      </div>

      <div
        class="car-gallery"
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="100"
      >
        <div class="main-image">
          <img
            :src="optimizedImages[selectedImageIndex]"
            :alt="`${car.manufacturer} ${car.model}`"
            loading="lazy"
            width="800"
            height="500"
          />
          <div class="image-overlay">
            <button
              class="gallery-nav prev"
              @click="prevImage"
              aria-label="Previous Image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button
              class="gallery-nav next"
              @click="nextImage"
              aria-label="Next Image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
        <div class="thumbnail-container">
          <div
            v-for="(image, index) in optimizedThumbnails"
            :key="index"
            class="thumbnail"
            :class="{ active: selectedImageIndex === index }"
            @click="selectImage(index)"
          >
            <img
              :src="image"
              :alt="`${car.manufacturer} ${car.model} thumbnail ${index + 1}`"
              loading="lazy"
              width="100"
              height="80"
            />
          </div>
        </div>
      </div>

      <div
        class="car-specs"
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="200"
      >
        <h2>Specifications</h2>
        <div class="specs-grid">
          <div class="spec-item">
            <div class="spec-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div class="spec-content">
              <h3>Year</h3>
              <p>{{ car.year }}</p>
            </div>
          </div>
          <div class="spec-item">
            <div class="spec-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                ></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div class="spec-content">
              <h3>Color</h3>
              <p>{{ car.color }}</p>
            </div>
          </div>
          <div class="spec-item">
            <div class="spec-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                <path d="M4 22h16"></path>
                <path
                  d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"
                ></path>
                <path
                  d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"
                ></path>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
              </svg>
            </div>
            <div class="spec-content">
              <h3>Fuel Type</h3>
              <p>{{ car.engine_type }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Lease Information Section -->
      <div
        v-if="car.is_lease && car.lease_info"
        class="lease-details"
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="400"
      >
        <h2>Lease Information</h2>
        <div class="lease-info-grid">
          <div v-if="car.lease_info.security_deposit" class="lease-info-item">
            <div class="lease-info-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
            </div>
            <div class="lease-info-content">
              <h3>Security Deposit</h3>
              <p>${{ car.lease_info.security_deposit }}</p>
            </div>
          </div>
          <div class="lease-info-item">
            <div class="lease-info-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                ></path>
              </svg>
            </div>
            <div class="lease-info-content">
              <h3>Mileage</h3>
              <p>{{ car.mileage_miles.toLocaleString() }} miles</p>
            </div>
          </div>

          <div class="lease-info-item">
            <div class="lease-info-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
            <div class="lease-info-content">
              <h3>Minimum Lease Period</h3>
              <p>
                {{ car.lease_info.minimum_lease_days }}
                {{
                  car.lease_info.minimum_lease_days === 1 ? "month" : "months"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <inquiry-form
        :car-id="carId"
        :base-url="baseUrl"
        :is-lease="car.is_lease"
        @inquiry-submitted="handleInquirySubmitted"
        data-aos="fade-up"
        data-aos-duration="1000"
        data-aos-delay="600"
      />
    </div>

    <transition name="modal">
      <div class="modal-overlay" v-if="showModal" @click.self="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Inquiry Submitted!</h3>
            <button class="modal-close" @click="closeModal" aria-label="Close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div class="success-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <p>
              Thank you! We've received your inquiry and will contact you
              shortly.
            </p>
          </div>
          <div class="modal-footer">
            <button class="modal-button" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";
import InquiryForm from "@/components/InquiryForm.vue";
import axios from "axios";
import API_CONFIG from "@/config/api.config.js";

export default {
  name: "CarDetail",
  components: {
    InquiryForm,
  },
  data() {
    return {
      selectedImageIndex: 0,
      car: null,
      loading: true,
      error: null,
      optimizedImages: [],
      optimizedThumbnails: [],
      imagesLoaded: 0,
      imageObserver: null,
      showModal: false,
      baseUrl: API_CONFIG.baseUrl,
    };
  },
  computed: {
    carId() {
      return this.$route.params.id;
    },
  },
  methods: {
    prevImage() {
      if (!this.car?.images?.length) return;
      this.selectedImageIndex =
        (this.selectedImageIndex - 1 + this.car.images.length) %
        this.car.images.length;
    },
    nextImage() {
      if (!this.car?.images?.length) return;
      this.selectedImageIndex =
        (this.selectedImageIndex + 1) % this.car.images.length;
    },
    selectImage(index) {
      this.selectedImageIndex = index;
    },
    handleInquirySubmitted() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    optimizeS3Images() {
      if (!this.car?.images?.length) {
        this.optimizedImages = [];
        this.optimizedThumbnails = [];
        return;
      }
      this.optimizedImages = this.car.images.map((path) => {
        if (path.startsWith("/static/upload/images/")) {
          return `${this.baseUrl}${path}`;
        }
        return `${this.baseUrl}/static/upload/images/${path}`;
      });
      this.optimizedThumbnails = [...this.optimizedImages];
      this.preloadImages();
    },
    preloadImages() {
      if (!this.optimizedImages.length) return;
      this.optimizedImages.forEach((src) => {
        const img = new Image();
        img.onload = () => {
          this.imagesLoaded++;
        };
        img.onerror = () => {
          console.error(`Failed to preload image: ${src}`);
        };
        img.src = src;
      });
    },
    setupLazyLoading() {
      if ("IntersectionObserver" in window) {
        this.imageObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const lazyImage = entry.target;
                const src = lazyImage.getAttribute("data-src");
                if (src) {
                  lazyImage.src = src;
                  lazyImage.removeAttribute("data-src");
                  this.imageObserver.unobserve(lazyImage);
                }
              }
            });
          },
          {
            rootMargin: "0px",
            threshold: 0.1,
          }
        );

        this.$nextTick(() => {
          const lazyImages = document.querySelectorAll("img[data-src]");
          lazyImages.forEach((img) => {
            this.imageObserver.observe(img);
          });
        });
      } else {
        this.loadAllImages();
      }
    },
    loadAllImages() {
      document.querySelectorAll("img[data-src]").forEach((img) => {
        img.src = img.getAttribute("data-src");
        img.removeAttribute("data-src");
      });
    },
    optimizeImageElement(img) {
      img.decoding = "async";
      img.loading = "lazy";
      img.onerror = () => {
        console.error("Failed to load image:", img.src);
        img.src =
          "https://www.shutterstock.com/image-vector/car-logo-icon-emblem-design-600nw-473088025.jpg";
      };
    },
    async fetchCarData() {
      this.loading = true;
      try {
        const response = await axios.get(
          `${this.baseUrl}/api/cars/${this.carId}`,
          { timeout: 10000 }
        );
        if (!response.data)
          throw new Error("No car data received from the server");
        this.car = response.data;
        this.optimizeS3Images();
        this.$nextTick(() => {
          this.setupLazyLoading();
        });
      } catch (err) {
        this.error =
          err.message || "Failed to load car data. Please try again.";
        console.error("Error fetching car data:", err);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchCarData();
  },
  mounted() {
    if (!sessionStorage.getItem("hasReloaded")) {
      sessionStorage.setItem("hasReloaded", "true");
      window.location.reload();
    } else {
      sessionStorage.removeItem("hasReloaded");
      AOS.init({
        once: true,
        offset: 50,
      });
      this.$nextTick(() => {
        document.querySelectorAll("img").forEach((img) => {
          this.optimizeImageElement(img);
        });
      });
    }
  },
  watch: {
    carId() {
      this.fetchCarData();
    },
  },
  beforeUnmount() {
    if (this.imageObserver) {
      this.imageObserver.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
.car-detail-section {
  position: relative;
  padding: 120px 0;
  color: #1a1a2e;

  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .section-header {
    text-align: center;
    margin-bottom: 4rem;

    .breadcrumb {
      font-size: 0.9rem;
      margin-bottom: 1rem;
      a {
        color: #ee800a;
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
      span {
        color: #4a4a4a;
      }
    }

    .section-title {
      font-size: 3rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: #09283f;
    }

    .title-underline {
      width: 80px;
      height: 3px;
      background: linear-gradient(to right, #ee800a, #ff7043);
      margin: 0 auto 1.5rem;
      border-radius: 2px;
    }

    .car-price {
      .price-tag {
        font-size: 2rem;
        font-weight: 600;
        color: #09283f;
        margin-right: 1rem;
      }
      .original-price {
        font-size: 1.2rem;
        color: #888;
        text-decoration: line-through;
      }
    }

    .car-lease-pricing {
      .lease-badge {
        display: inline-block;
        background: linear-gradient(135deg, #ee800a, #ff7043);
        color: white;
        padding: 0.5rem 1.5rem;
        border-radius: 50px;
        font-size: 0.9rem;
        font-weight: 600;
        margin-bottom: 1rem;
        box-shadow: 0 4px 15px rgba(238, 128, 10, 0.3);
      }

      .lease-prices {
        display: flex;
        justify-content: center;
        gap: 2rem;
        flex-wrap: wrap;

        .lease-price-option {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;

          .price-label {
            font-size: 0.9rem;
            color: #4a4a4a;
            font-weight: 500;
          }

          .price-value {
            font-size: 1.8rem;
            font-weight: 700;
            color: #09283f;
            background: linear-gradient(135deg, #ee800a, #ff7043);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
        }
      }
    }
  }

  .car-gallery {
    margin-bottom: 5rem;

    .main-image {
      position: relative;
      margin-bottom: 1.5rem;
      img {
        width: 100%;
        height: auto;
        border-radius: 10px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      }

      .image-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 1rem;

        .gallery-nav {
          background: rgba(255, 255, 255, 0.9);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            width: 20px;
            height: 20px;
            color: #09283f;
          }

          &:hover {
            background: #ee800a;
            svg {
              color: white;
            }
          }
        }
      }
    }

    .thumbnail-container {
      display: flex;
      gap: 1rem;
      overflow-x: auto;
      padding-bottom: 1rem;

      .thumbnail {
        flex: 0 0 100px;
        cursor: pointer;
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.3s ease;

        img {
          width: 100%;
          height: 80px;
          object-fit: cover;
        }

        &.active,
        &:hover {
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          transform: translateY(-5px);
          border: 2px solid #ee800a;
        }
      }
    }
  }

  .car-specs {
    margin-bottom: 5rem;

    h2 {
      font-size: 2.2rem;
      font-weight: 600;
      margin-bottom: 2rem;
      color: #09283f;
      position: relative;
      padding-bottom: 1rem;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: 60px;
        height: 3px;
        background: linear-gradient(to right, #ee800a, #ff7043);
        border-radius: 2px;
      }
    }

    .specs-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 2rem;

      .spec-item {
        display: flex;
        align-items: center;
        padding: 1.5rem;
        background-color: #f8f9fa;
        border-radius: 10px;
        transition: all 0.3s ease;

        &:hover {
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
          transform: translateY(-5px);
        }

        .spec-icon {
          width: 50px;
          height: 50px;
          background-color: rgba(238, 128, 10, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;

          svg {
            width: 24px;
            height: 24px;
            color: #ee800a;
          }
        }

        .spec-content {
          h3 {
            font-size: 0.9rem;
            color: #4a4a4a;
            margin-bottom: 0.3rem;
          }

          p {
            font-size: 1.2rem;
            font-weight: 600;
            color: #09283f;
            margin: 0;
          }
        }
      }
    }
  }

  .lease-details {
    margin-bottom: 5rem;

    h2 {
      font-size: 2.2rem;
      font-weight: 600;
      margin-bottom: 2rem;
      color: #09283f;
      position: relative;
      padding-bottom: 1rem;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        width: 60px;
        height: 3px;
        background: linear-gradient(to right, #ee800a, #ff7043);
        border-radius: 2px;
      }
    }

    .lease-info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 2rem;

      .lease-info-item {
        display: flex;
        align-items: center;
        padding: 1.5rem;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border-radius: 10px;
        border: 1px solid rgba(238, 128, 10, 0.1);
        transition: all 0.3s ease;

        &:hover {
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
          transform: translateY(-5px);
          border-color: rgba(238, 128, 10, 0.3);
        }

        .lease-info-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(
            135deg,
            rgba(238, 128, 10, 0.1),
            rgba(255, 112, 67, 0.1)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          flex-shrink: 0;

          svg {
            width: 24px;
            height: 24px;
            color: #ee800a;
          }
        }

        .lease-info-content {
          flex: 1;

          h3 {
            font-size: 0.9rem;
            color: #4a4a4a;
            margin-bottom: 0.3rem;
            font-weight: 500;
          }

          p {
            font-size: 1.2rem;
            font-weight: 600;
            color: #09283f;
            margin: 0;

            &.availability-status {
              color: #dc3545;

              &.available {
                color: #28a745;
              }
            }
          }
        }
      }
    }
  }

  .error-message {
    color: #ff4444;
    background-color: #ffe6e6;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 0.9rem;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 15px;
    width: 90%;
    max-width: 500px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    position: relative;
    animation: modalAppear 0.3s ease-out;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;

    h3 {
      font-size: 1.8rem;
      color: #09283f;
      margin: 0;
    }

    .modal-close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      transition: all 0.3s ease;

      svg {
        width: 24px;
        height: 24px;
        color: #4a4a4a;
      }

      &:hover {
        transform: rotate(90deg);
        svg {
          color: #ee800a;
        }
      }
    }
  }

  .modal-body {
    text-align: center;
    margin-bottom: 2rem;

    .success-icon {
      width: 60px;
      height: 60px;
      background: rgba(238, 128, 10, 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;

      svg {
        width: 30px;
        height: 30px;
        color: #ee800a;
      }
    }

    p {
      font-size: 1.1rem;
      color: #4a4a4a;
      margin: 0;
    }
  }

  .modal-footer {
    text-align: center;

    .modal-button {
      background: linear-gradient(to right, #ee800a, #ff7043);
      color: white;
      border: none;
      padding: 0.8rem 2rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 50px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 5px 15px rgba(238, 128, 10, 0.3);
        transform: translateY(-2px);
      }
    }
  }

  .modal-enter-active,
  .modal-leave-active {
    transition: opacity 0.3s ease;
  }

  .modal-enter-from,
  .modal-leave-to {
    opacity: 0;
  }

  @keyframes modalAppear {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .loading-skeleton {
    width: 100%;
    padding: 2rem 0;

    .skeleton-header {
      margin-bottom: 4rem;
      text-align: center;
    }

    .skeleton-breadcrumb {
      width: 200px;
      height: 14px;
      margin: 0 auto 1rem;
    }

    .skeleton-title {
      width: 60%;
      height: 36px;
      margin: 0 auto 1rem;
    }

    .skeleton-underline {
      width: 80px;
      height: 3px;
      margin: 0 auto 1.5rem;
    }

    .skeleton-price {
      width: 150px;
      height: 24px;
      margin: 0 auto;
    }

    .skeleton-gallery {
      margin-bottom: 5rem;
    }

    .skeleton-main-image {
      width: 100%;
      height: 500px;
      margin-bottom: 1.5rem;
      border-radius: 10px;
    }

    .skeleton-thumbnails {
      display: flex;
      gap: 1rem;
      overflow: hidden;
    }

    .skeleton-thumbnail {
      width: 100px;
      height: 80px;
      border-radius: 8px;
      flex-shrink: 0;
    }

    .skeleton-specs {
      margin-bottom: 5rem;
    }

    .skeleton-specs-title {
      width: 200px;
      height: 28px;
      margin-bottom: 2rem;
    }

    .skeleton-specs-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 2rem;
    }

    .skeleton-spec-item {
      height: 80px;
      border-radius: 10px;
    }

    .skeleton-form {
      .skeleton-form-title {
        width: 200px;
        height: 28px;
        margin-bottom: 2rem;
      }

      .skeleton-form-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
        margin-bottom: 2rem;
      }

      .skeleton-input {
        height: 48px;
        border-radius: 8px;
      }

      .skeleton-button {
        width: 200px;
        height: 48px;
        margin: 0 auto;
        border-radius: 50px;
      }
    }

    .skeleton {
      background: #f0f0f0;
      position: relative;
      overflow: hidden;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 0.4),
          rgba(255, 255, 255, 0)
        );
        animation: shimmer 1.5s infinite;
        transform: translateX(-100%);
      }
    }

    @keyframes shimmer {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }
  }
}

@media (max-width: 992px) {
  .car-detail-section {
    padding: 100px 0;
    .section-header {
      .section-title {
        font-size: 2.5rem;
      }
      .car-lease-pricing .lease-prices {
        gap: 1rem;
      }
    }
    .car-specs .specs-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .lease-details .lease-info-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (max-width: 768px) {
  .car-detail-section {
    padding: 80px 0;
    .section-header {
      .section-title {
        font-size: 2.2rem;
      }
      .car-price .price-tag {
        font-size: 1.6rem;
      }
      .car-lease-pricing {
        .lease-badge {
          font-size: 0.85rem;
          padding: 0.4rem 1.2rem;
        }
        .lease-prices {
          flex-direction: column;
          gap: 1rem;
          .lease-price-option .price-value {
            font-size: 1.5rem;
          }
        }
      }
    }
    .car-specs .specs-grid {
      grid-template-columns: 1fr;
    }
    .lease-details .lease-info-grid {
      grid-template-columns: 1fr;
    }
    .car-gallery .thumbnail-container .thumbnail {
      flex: 0 0 80px;
      img {
        height: 60px;
      }
    }
    .loading-skeleton {
      .skeleton-main-image {
        height: 300px;
      }
      .skeleton-specs-grid {
        grid-template-columns: 1fr;
      }
      .skeleton-form-grid {
        grid-template-columns: 1fr;
      }
    }
  }
}

@media (max-width: 480px) {
  .car-detail-section {
    padding: 60px 0;
    .container {
      padding: 0 1.5rem;
    }
    .section-header {
      .section-title {
        font-size: 1.8rem;
      }
      .car-price .price-tag {
        font-size: 1.4rem;
        display: block;
        margin-bottom: 0.5rem;
      }
      .car-lease-pricing {
        .lease-prices .lease-price-option {
          .price-label {
            font-size: 0.85rem;
          }
          .price-value {
            font-size: 1.3rem;
          }
        }
      }
    }
    h2 {
      font-size: 1.8rem !important;
    }
  }
}
</style>
